import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { fmtDateTime } from "../../design/domain";
import ListPager from "../../components/design/ListPager";
import { Banner, EmptyState } from "../../components/mui";
import { PageLoading, SkeletonRows } from "../../components/loading";
import { useApp } from "../../design/AppContext";
import { apiDelete, apiGet, apiPostJson, apiPutJson } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { hasPermission } from "../../utils/permission";
import {
  GATE,
  ITEM_STATUS,
  RISK,
  isActiveStatus,
  fmtDuration,
  fmtCount,
  safeHttpUrl,
  parseListLines,
  MetaPill,
  RunStatusPill,
  GatePill,
  IconButton,
  KpiRow,
  ScanDrawer,
  MetaGrid,
  ErrorNote,
  Disclosure,
  LogTail,
  SectionHead,
  CountChip,
  HeadNote,
  tableBodySx,
  useScanPage,
  useRunHistory,
  useRunReport,
} from "../../components/scan/ScanKit";
import { MdPlayArrow } from "react-icons/md";

/**
 * 專案原碼檢測（SonarQube）
 *
 * 選專案 → 維護 Git 連結 → 開始檢測 → 看歷史與報告。
 * 檢測本身在另一台檢測主機的 worker 上跑（worker 透過 /ScanWorker API 領工作、回報結果），
 * 這一頁只負責排入佇列與呈現：按下「開始檢測」只是排隊，要等 worker 領走才會真的開始。
 *
 * 選哪個專案、開哪份報告都記在網址列（?projectId=&run=），站內通知的連結直接帶進來。
 * 從「檢測」頁（/Scan）點某一個 repo 進來時多帶一個 &repo=（result id），報告打開後
 * 直接捲到那個 repo、問題清單已經展開。
 *
 * 報告裡每個 repo 的問題清單是存在 iProject 裡的那一份（最多 100 筆，見 SONAR_ISSUE_CAP），
 * 可以直接在站內看、也可以下載成 CSV；SonarQube 的原始頁面是公司內網網址，只留作次要連結。
 */

const BASE = "/CodeScan";

/* ===================== 樣式 ===================== */

const MONO = { fontFamily: "var(--font-mono)" };

/* ===================== 對照表 ===================== */

const BUILD_TOOL = { dotnet: ".NET（先建置再分析）", cli: "sonar-scanner" };
const ISSUE_TYPE = { BUG: "Bug", VULNERABILITY: "弱點", CODE_SMELL: "Code Smell" };

/** SonarQube 嚴重度沿用弱點掃描的風險色階，兩頁看到的「紅／橘／黃褐／灰」意思一致 */
const SEVERITY = {
  BLOCKER: { ...RISK[3], label: "阻斷" },
  CRITICAL: { ...RISK[3], label: "嚴重" },
  MAJOR: { ...RISK[2], label: "主要" },
  MINOR: { ...RISK[1], label: "次要" },
  INFO: { ...RISK[0], label: "提示" },
};

/** SonarQube 評級 A（最好）～E（最差）的色塊 */
const RATING_C = { A: "#0e7c66", B: "#5b9a3c", C: "#b7791f", D: "#dc6803", E: "#d92d20" };

/* ===================== 工具 ===================== */

const repoLine = (r) => (r.branch ? `${r.url} ${r.branch}` : r.url);
const reposToText = (repos) => repos.map(repoLine).join("\n");

/** 判斷有沒有未儲存的變更時，略過註解與空行：只加了一行 # 備註不算改過清單 */
const normalizedDraft = (text) =>
  parseListLines(text)
    .map((x) => (x.rest ? `${x.url} ${x.rest}` : x.url))
    .join("\n");

const pct = (v) => (v === null || v === undefined || v === "" ? "—" : `${Number(v).toFixed(1)}%`);

/** 大於 0 才標紅：Bug 與弱點是「有就該處理」的數字 */
const alertColor = (v) => (Number(v) > 0 ? "error.main" : undefined);

/** Sonar 的 component 是「projectKey:路徑」，畫面上只需要路徑 */
const fileOf = (component) => String(component || "").replace(/^[^:]*:/, "");

/** 評級應該已由 worker 轉成 A–E；萬一拿到 Sonar 原始的 1.0–5.0 也照樣換算 */
const ratingLetter = (v) => {
  const s = String(v == null ? "" : v).trim();
  if (/^[1-5](\.0+)?$/.test(s)) return "ABCDE"[Math.round(Number(s)) - 1];
  const up = s.toUpperCase();
  return RATING_C[up] ? up : "";
};

/* ===================== 問題清單：存了多少、下載成檔案 ===================== */

/*
 * 為什麼要有這一段：SonarQube 的網址（dashboard_url）是公司內網位址，人在外面按下去
 * 什麼都打不開。但問題清單其實早就存在 iProject 自己的資料庫裡（IssuesJson），
 * 所以改成在站內看、也可以下載成檔案 —— 不必為此新增任何後端端點。
 *
 * 這幾個函式有 export：「檢測」合併頁（pages/Scan/Index.js）的明細抽屜也要用同一套
 * 「存了幾筆、少了幾筆」的判斷與同一份檔案格式。照理應該放在 components/scan/ScanKit.js，
 * 這一輪不能動那個檔，先放在這裡；兩頁各留一份的話，上限數字遲早會有一邊忘了改。
 */

/**
 * iProject 存下來的問題清單，每個 repo 最多幾筆。
 *
 * 不是前端自己訂的：worker（api/Worker/Services/SonarService.cs 的 FetchIssuesAsync）
 * 向 SonarQube 只要一頁 —— ps=100、依嚴重度由高到低（s=SEVERITY&asc=false）—— 存成 IssuesJson。
 * 所以畫面上寫 Code Smell 116 個的 repo，清單與檔案都只會有 100 筆。後端改了頁數，這裡要一起改。
 */
export const SONAR_ISSUE_CAP = 100;

const hasNum = (v) => v !== null && v !== undefined && v !== "";

/**
 * 一個 repo「存了幾筆」對上「SonarQube 統計有幾個」。
 *
 * total 用 Bug＋弱點＋Code Smell 三個指標加總：api/issues/search 回的就是這三類，
 * Security Hotspot 從 SonarQube 8.2 起是另一支 API，本來就不在清單裡，所以不加。
 * 三個裡只要有一個是 null（失敗、還沒跑完）就當作不知道總數 —— 拿半套數字去比，
 * 會把「不知道」講成「少了幾筆」。
 *
 * 不知道總數時，只能從「剛好存滿上限」推測可能不完整；知道總數時以總數為準。
 */
export function issueCoverage(r) {
  const row = r || {};
  const stored = Array.isArray(row.issues) ? row.issues.length : 0;
  const parts = [row.bugs, row.vulnerabilities, row.code_smells];
  const total = parts.every(hasNum) ? parts.reduce((s, v) => s + Number(v), 0) : null;
  return {
    stored,
    total,
    /* 存滿上限：清單是被截掉的，截掉的是嚴重度比較低的那些 */
    capped: stored >= SONAR_ISSUE_CAP,
    truncated: total === null ? stored >= SONAR_ISSUE_CAP : total > stored,
    missing: total === null ? null : Math.max(0, total - stored),
  };
}

/**
 * 把 issueCoverage 念成一句話。畫面上的提示條與檔案開頭都用這一句，兩邊講的才會一樣。
 *
 * total 比 stored 多、但又沒存滿上限時，不是被 100 筆截掉的：多半是檢測時讀問題清單失敗
 * （worker 失敗會略過、不讓整個 repo 失敗，執行紀錄裡有寫），或兩邊的算法不完全一樣。
 * 猜不準是哪一個，所以兩個都講、並說以 SonarQube 為準，不假裝知道原因。
 */
export function issueCoverageText(cov) {
  const { stored, total, capped, missing } = cov;
  if (total !== null && total > stored) {
    if (capped) {
      return `SonarQube 統計共 ${fmtCount(total)} 個問題，iProject 僅儲存嚴重度最高的 ${fmtCount(stored)} 筆，缺少 ${fmtCount(missing)} 筆。`;
    }
    return `SonarQube 統計共 ${fmtCount(total)} 個問題，但 iProject 僅儲存 ${fmtCount(stored)} 筆，缺少 ${fmtCount(missing)} 筆。可能原因為檢測時讀取問題清單失敗（詳見執行紀錄），或兩者計算方式不同；請以 SonarQube 為準。`;
  }
  if (total !== null && total < stored) {
    return `已儲存 ${fmtCount(stored)} 筆，但 SonarQube 統計為 ${fmtCount(total)} 個，兩者不一致；請以 SonarQube 為準。`;
  }
  if (total === 0) return "SonarQube 未發現未解決的問題。";
  if (total !== null) return `${fmtCount(stored)} 筆，與 SonarQube 統計一致，清單完整。`;
  if (capped) return `已儲存 ${fmtCount(stored)} 筆，已達上限；本次未取得 SonarQube 總數，實際問題可能更多。`;
  return `已儲存 ${fmtCount(stored)} 筆；本次未取得 SonarQube 總數，無法確認是否完整。`;
}

/**
 * 整批提示條（/Scan 明細抽屜、這一頁的報告抽屜）用的原因說明：只看檢測成功的 repo。
 *
 * truncated 只是「清單比統計少」，原因不只一種，不能全部講成「被 100 筆上限截掉」：
 *   · capped：存滿上限，而且知道總數比較多 —— 確定是上限截掉的；
 *   · maybeCapped：存滿上限、但沒拿到總數 —— 可能剛好就是全部，只能說「可能」；
 *   · short：沒存滿上限卻比統計少 —— 不是上限，是讀清單失敗或兩邊算法不同
 *     （跟 issueCoverageText 各 repo 那一句同一套說法，整批和單一 repo 才不會互相矛盾）。
 * 回傳 null 表示沒有任何 repo 需要提示（規則第 2 條：空的不畫）。
 */
export function coverageBanner(results) {
  let capped = 0;
  let maybeCapped = 0;
  let short = 0;
  (results || []).forEach((r) => {
    if (Number(r.status) !== 2) return;
    const c = issueCoverage(r);
    if (!c.truncated) return;
    if (c.capped && c.total === null) maybeCapped += 1;
    else if (c.capped) capped += 1;
    else short += 1;
  });
  if (capped + maybeCapped + short === 0) return null;
  const reasons = [];
  if (capped + maybeCapped > 0) {
    reasons.push(
      `有 ${capped + maybeCapped} 個 repo 已達儲存上限：iProject 每個 repo 最多僅儲存嚴重度最高的 ${SONAR_ISSUE_CAP} 筆問題` +
        (maybeCapped > 0 ? `（其中 ${maybeCapped} 個本次未取得 SonarQube 總數，也可能恰為完整清單）` : "") +
        "。"
    );
  }
  if (short > 0) {
    reasons.push(
      `有 ${short} 個 repo 儲存的筆數少於 SonarQube 統計，但未達上限，並非因上限截斷：可能原因為檢測時讀取問題清單失敗（詳見執行紀錄，此情況重新檢查後可能即可取得），或兩者計算方式不同；請以 SonarQube 為準。`
    );
  }
  return {
    /* 只有「沒拿到總數」的那種，不能斷定不是全部 */
    sure: capped + short > 0,
    count: capped + maybeCapped + short,
    reasons,
  };
}

/**
 * 試算表會把 = + - @（以及 Tab、CR）開頭的格子當成公式執行（CSV injection）。
 * 訊息與檔案路徑是被檢測的程式碼決定的，等於任何能推 code 的人都能往這份檔案裡塞內容，
 * 所以這種格子前面補一個 ' 讓 Excel 當成文字 —— 代價是那一格開頭會多一個 '，比被執行好。
 * 只對字串做：行號、序號是數字，不會碰到。
 */
const CSV_FORMULA_START = /^[=+\-@\t\r]/;

/**
 * RFC 4180：欄位裡有逗號、雙引號、換行就整格用雙引號包起來，裡面的雙引號寫兩次。
 * SonarQube 的訊息常有逗號（「Remove this unused import, …」），不跳脫的話整張表會錯欄。
 */
function csvCell(v) {
  if (v === null || v === undefined) return "";
  let s = String(v);
  if (typeof v === "string" && CSV_FORMULA_START.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const csvRow = (cells) => cells.map(csvCell).join(",");

/** 檔名只需要 repo 的名字：「git@host:group/api.git」→「api」 */
function repoName(url) {
  const s = String(url || "")
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "");
  return (s.split(/[/:]/).pop() || "").replace(/\.git$/i, "") || "repo";
}

/** Windows 不允許的字元與空白一律換成「-」，太長的截掉，免得存檔時被瀏覽器改名 */
const fileSafe = (s) =>
  String(s || "")
    .replace(/[\\/:*?"<>|\s]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

/** 檔案開頭每個 repo 一行：這個 repo 的清單完不完整 */
function repoCoverageLine(r) {
  const st = Number(r.status);
  if (st !== 2) {
    const label = ITEM_STATUS[st] ? ITEM_STATUS[st].label : "狀態不明";
    return `此 repo 檢測未成功（${label}），無問題清單。`;
  }
  return issueCoverageText(issueCoverage(r));
}

/**
 * 組出 CSV 內容。
 *
 * 檔案開頭先寫幾行說明，再空一行才是表格 —— 「最多 100 筆」這件事要寫在打開檔案第一眼
 * 就看得到的地方，不能只寫在網頁上：檔案會被轉寄，收到的人沒看過這一頁。
 * 開頭加 UTF-8 BOM，否則 Excel 會用系統編碼（Big5）開，中文全變亂碼。
 * 換行用 CRLF（RFC 4180）。
 */
function buildIssuesCsv({ run, project, results }) {
  const lines = [
    csvRow([
      "說明",
      `本檔案為 iProject 於檢測時儲存的 SonarQube 問題清單，不一定完整：每個 repo 最多僅儲存嚴重度最高的 ${SONAR_ISSUE_CAP} 筆。各 repo 缺少的筆數列於下方。`,
    ]),
  ];
  if (project) lines.push(csvRow(["專案", [project.code, project.name].filter(Boolean).join(" ")]));
  if (run) lines.push(csvRow(["檢測批次", `#${run.id}（${fmtDateTime(run.finished_at || run.queued_at)}）`]));
  lines.push(csvRow(["下載時間", fmtDateTime(new Date())]));
  results.forEach((r) => lines.push(csvRow(["Repo", `${r.repo_url || ""}（${r.branch || "預設分支"}）`, repoCoverageLine(r)])));
  lines.push("");

  lines.push(csvRow(["序號", "嚴重度", "類型", "訊息", "檔案", "行號", "規則", "Repo", "分支", "SonarQube 問題代碼"]));
  let n = 0;
  results.forEach((r) => {
    (Array.isArray(r.issues) ? r.issues : []).forEach((i) => {
      n += 1;
      const sevKey = String(i.severity || "").toUpperCase();
      const sev = SEVERITY[sevKey];
      lines.push(
        csvRow([
          n,
          /* 中文給人看、原文給要回 SonarQube 對照的人看 */
          sev ? `${sev.label}（${sevKey}）` : sevKey,
          ISSUE_TYPE[i.type] || i.type || "",
          i.message || "",
          fileOf(i.component),
          i.line === null || i.line === undefined ? "" : Number(i.line),
          i.rule || "",
          r.repo_url || "",
          r.branch || "預設分支",
          i.key || "",
        ])
      );
    });
  });
  return `﻿${lines.join("\r\n")}\r\n`;
}

/**
 * 檔名也要講實話：存滿上限的寫「只有前 100 筆」，知道總數的連總數一起寫。
 * 檔案被存到下載資料夾之後，檔名是唯一一個不用打開就看得到的地方。
 */
function issuesCsvFileName({ run, project, results }) {
  const covs = results.map(issueCoverage);
  const stored = covs.reduce((s, c) => s + c.stored, 0);
  let who;
  let tail;
  if (results.length === 1) {
    const c = covs[0];
    who = repoName(results[0].repo_url);
    if (c.truncated && c.total !== null) tail = `僅${c.stored}筆-SonarQube共${c.total}個`;
    else if (c.truncated) tail = `僅前${c.stored}筆-可能不完整`;
    else tail = `${c.stored}筆`;
  } else {
    who = `${results.length}個repo`;
    tail = covs.some((c) => c.truncated) ? `共${stored}筆-不完整` : `共${stored}筆`;
  }
  const parts = ["SonarQube問題清單", project && project.code, run && `檢測${run.id}`, who, tail];
  return `${parts.filter(Boolean).map(fileSafe).join("_")}.csv`;
}

/**
 * 在瀏覽器裡直接產生檔案下載，不經過後端 —— 資料就是畫面上這份報告已經拿到的問題清單。
 * results 傳一個 repo 就只下載那一個，傳整批就合成一份（多一欄 Repo 分得出來）。
 */
export function downloadIssuesCsv(ctx) {
  const blob = new Blob([buildIssuesCsv(ctx)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = issuesCsvFileName(ctx);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  /* 晚一點才收回：有些瀏覽器是 click 之後才非同步開始讀 blob，立刻 revoke 會存到空檔 */
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 有沒有任何一個 repo 存了問題 —— 一筆都沒有時不畫下載鈕（下載一份只有表頭的檔案沒有意義） */
export const hasStoredIssues = (results) => results.some((r) => Array.isArray(r.issues) && r.issues.length > 0);

/* ===================== 報告抽屜 ===================== */

function RatingBadge({ label, value }) {
  const letter = ratingLetter(value);
  const c = RATING_C[letter];
  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      title={letter ? `${label}評級 ${letter}` : `${label}：無評級`}
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontWeight: 700 }}
    >
      {label}
      <Box
        component="span"
        sx={{
          /* 方塊與字母一起跟著字級縮放：em 的基準是元素自己的 font-size，
             一般模式仍是 22px，大字模式整顆一起放大（同 design/ui.js 的頭像） */
          width: "1.77em",
          height: "1.77em",
          borderRadius: "var(--radius-sm)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "calc(var(--t-meta) * 0.83)",
          fontWeight: 900,
          color: c ? "#fff" : "text.disabled",
          bgcolor: c || "action.hover",
        }}
      >
        {letter || "—"}
      </Box>
    </Typography>
  );
}

RatingBadge.propTypes = { label: PropTypes.string, value: PropTypes.string };
RatingBadge.defaultProps = { label: "", value: "" };

function IssueList({ issues, emptyText }) {
  if (issues.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <div>
      {issues.map((i, idx) => {
        const sev = SEVERITY[String(i.severity || "").toUpperCase()] || SEVERITY.INFO;
        const file = fileOf(i.component);
        return (
          <Box
            key={i.key || idx}
            sx={{
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              py: 1.25,
              ...(idx ? { borderTop: 1, borderColor: "divider" } : {}),
            }}
          >
            <MetaPill meta={sev} title={String(i.severity || "")} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, lineHeight: 1.5, wordBreak: "break-word" }}>{i.message}</Typography>
              <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mt: 0.5 }}
              >
                <span>{ISSUE_TYPE[i.type] || i.type}</span>
                {file && (
                  <Box component="span" sx={{ ...MONO, wordBreak: "break-all" }}>
                    {file}
                    {i.line ? `:${i.line}` : ""}
                  </Box>
                )}
                {i.rule && (
                  <Box component="span" sx={MONO}>
                    {i.rule}
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </div>
  );
}

IssueList.propTypes = { issues: PropTypes.arrayOf(PropTypes.object), emptyText: PropTypes.string };
IssueList.defaultProps = { issues: [], emptyText: "無問題項目" };

function RepoResultCard({ result: r, focused, onDownload }) {
  const st = Number(r.status);
  const issues = Array.isArray(r.issues) ? r.issues : [];
  const link = safeHttpUrl(r.dashboard_url);
  const cov = issueCoverage(r);
  const cardRef = useRef(null);

  /* 報告每 5 秒輪詢時卡片不會重掛（key 是 result id），所以只會在打開時捲這一次，不會一直把人拉回來 */
  useEffect(() => {
    if (focused && cardRef.current) cardRef.current.scrollIntoView({ block: "start" });
  }, [focused]);

  const metrics = [
    { label: "Bug", value: fmtCount(r.bugs), color: alertColor(r.bugs) },
    { label: "弱點", value: fmtCount(r.vulnerabilities), color: alertColor(r.vulnerabilities) },
    { label: "Code Smell", value: fmtCount(r.code_smells) },
    { label: "Hotspot", value: fmtCount(r.security_hotspots) },
    { label: "覆蓋率", value: pct(r.coverage) },
    { label: "重複率", value: pct(r.duplication) },
    { label: "程式行數", value: fmtCount(r.ncloc) },
  ];
  // 失敗或還沒分析完的 repo 整排都是「—」，不如不畫
  const hasMetrics = [r.bugs, r.vulnerabilities, r.code_smells, r.security_hotspots, r.coverage, r.duplication, r.ncloc].some(
    (v) => v !== null && v !== undefined
  );
  const hasRatings = [r.reliability_rating, r.security_rating, r.maintainability_rating].some(Boolean);
  // 執行中把目前步驟併進膠囊（「執行中・建置中」），一眼看出卡在哪
  const statusLabel = st === 1 && r.stage ? `${ITEM_STATUS[1].label}・${r.stage}` : "";

  /*
   * 清單與統計對不起來時一定要講（硬規則 4：寫成提示條，不寫成灰字）：
   * 最常見的是存滿 100 筆被截掉 —— 畫面上 Code Smell 116 個，清單只有 100 筆，
   * 不講的話使用者會以為下載的就是全部。只在檢測成功（status 2）時才比，
   * 失敗、執行中的 repo 本來就沒有清單，拿來比只會多一條沒意義的警告。
   */
  const covMismatch = st === 2 && (cov.truncated || (cov.total !== null && cov.total !== cov.stored));
  let covTitle = "問題清單與統計數字不一致";
  if (cov.truncated && cov.capped) covTitle = `僅儲存嚴重度最高的 ${fmtCount(cov.stored)} 筆`;
  else if (cov.truncated) covTitle = "問題清單少於統計數字";

  let emptyText = "未儲存任何問題";
  if (st === 2 && cov.total === 0) emptyText = "SonarQube 未發現未解決的問題";
  else if (st !== 2) emptyText = "此 repo 檢測未成功，因此無問題清單";

  return (
    <Card
      ref={cardRef}
      sx={{
        bgcolor: "background.default",
        overflow: "visible",
        /* 從「檢測」頁點進來的那一張，左邊多一條主色，捲過來時一眼認得出是哪一張 */
        ...(focused
          ? { borderColor: "primary.main", boxShadow: (t) => `inset 4px 0 0 ${t.palette.primary.main}` }
          : {}),
      }}
    >
      <Stack spacing={1.5} sx={{ px: 2, py: 1.75 }}>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ ...MONO, fontSize: "var(--t-code)", fontWeight: 700, wordBreak: "break-all" }}>
              {r.repo_url}
            </Typography>
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.75 }}
            >
              <span>
                分支{" "}
                <Box component="span" sx={{ ...MONO, color: "text.primary", fontWeight: 700 }}>
                  {r.branch || "預設分支"}
                </Box>
              </span>
              {r.commit_sha && (
                <span title={r.commit_sha}>
                  commit{" "}
                  <Box component="span" sx={{ ...MONO, color: "text.primary", fontWeight: 700 }}>
                    {String(r.commit_sha).slice(0, 8)}
                  </Box>
                </span>
              )}
              {r.build_tool && <span>建置方式 {BUILD_TOOL[r.build_tool] || r.build_tool}</span>}
              {r.analysis_at && <span>分析於 {fmtDateTime(r.analysis_at)}</span>}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
            <MetaPill meta={ITEM_STATUS[st]} dot={st === 1} label={statusLabel} />
            <GatePill gate={r.quality_gate || ""} />
          </Box>
        </Box>

        {hasMetrics && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(92px,1fr))",
              gap: "1px",
              bgcolor: "divider",
              border: 1,
              borderColor: "divider",
            }}
          >
            {metrics.map((m) => (
              <Box key={m.label} sx={{ bgcolor: "background.paper", px: 1.25, py: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: 700 }}>
                  {m.label}
                </Typography>
                <Typography
                  sx={{ fontWeight: 800, mt: 0.25, fontVariantNumeric: "tabular-nums", color: m.color || "text.primary" }}
                >
                  {m.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {hasRatings && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
            <RatingBadge label="可靠性" value={r.reliability_rating || ""} />
            <RatingBadge label="安全性" value={r.security_rating || ""} />
            <RatingBadge label="可維護性" value={r.maintainability_rating || ""} />
          </Box>
        )}

        {covMismatch && (
          <Banner kind="warn" title={covTitle}>
            {issueCoverageText(cov)}
            {cov.truncated && `下方清單與下載的檔案僅包含這 ${fmtCount(cov.stored)} 筆，其餘項目僅能於 SonarQube 查看。`}
          </Banner>
        )}

        {/*
          這兩顆按鈕允許換行：抽屜在手機上只有三百多 px 寬，
          這兩句一行放不下會把抽屜撐出橫向捲軸。
        */}
        {(issues.length > 0 || link) && (
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            {issues.length > 0 && (
              <Button variant="outlined" sx={{ whiteSpace: "normal" }} onClick={() => onDownload(r)}>
                下載此 repo 的問題清單（{fmtCount(cov.stored)} 筆，可用 Excel 開啟）
              </Button>
            )}
            {link && (
              <Button variant="text" sx={{ whiteSpace: "normal" }} href={link} target="_blank" rel="noopener noreferrer">
                至 SonarQube 檢視原始頁面
              </Button>
            )}
            {link && (
              <Typography component="span" variant="body2" color="text.secondary">
                SonarQube 為公司內網網址，無法從外部網路開啟。
              </Typography>
            )}
          </Stack>
        )}

        <ErrorNote text={r.error_message || ""} />
      </Stack>
      <Box sx={{ px: 2, pb: 1 }}>
        {/* 括號裡是存下來的筆數，不是 SonarQube 的總數；兩個不一樣時上面的提示條會講 */}
        <Disclosure label="問題清單" count={issues.length} defaultOpen={Boolean(focused) && issues.length > 0}>
          {issues.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              依嚴重度由高到低排列
              {cov.capped ? `；iProject 最多僅儲存 ${SONAR_ISSUE_CAP} 筆，此處為前 ${fmtCount(cov.stored)} 筆` : ""}。
            </Typography>
          )}
          <IssueList issues={issues} emptyText={emptyText} />
        </Disclosure>
        <Disclosure label="執行紀錄">
          <LogTail text={r.log_tail || ""} />
        </Disclosure>
      </Box>
    </Card>
  );
}

RepoResultCard.propTypes = {
  result: PropTypes.shape({
    id: PropTypes.number,
    repo_url: PropTypes.string,
    branch: PropTypes.string,
    commit_sha: PropTypes.string,
    status: PropTypes.number,
    stage: PropTypes.string,
    build_tool: PropTypes.string,
    quality_gate: PropTypes.string,
    dashboard_url: PropTypes.string,
    error_message: PropTypes.string,
    log_tail: PropTypes.string,
    issues: PropTypes.array,
  }),
  focused: PropTypes.bool,
  onDownload: PropTypes.func,
};

RepoResultCard.defaultProps = { result: {}, focused: false, onDownload: () => {} };

function CodeReportDrawer({ open, runId, focusRepoId, onClose }) {
  const { report, loading, error } = useRunReport(BASE, open ? runId : null);
  const run = report && report.run ? report.run : null;
  const proj = report && report.project ? report.project : null;
  const results = report && Array.isArray(report.results) ? report.results : [];
  const covBanner = coverageBanner(results);

  let body;
  if (error) {
    body = <EmptyState title="報告載入失敗" text={error} />;
  } else if (!run) {
    body = loading ? <PageLoading dense /> : <EmptyState dense text="找不到此報告" />;
  } else {
    body = (
      <Stack spacing={2}>
        <MetaGrid
          items={[
            { label: "排入", value: fmtDateTime(run.queued_at) },
            { label: "開始", value: fmtDateTime(run.started_at) },
            { label: "結束", value: fmtDateTime(run.finished_at) },
            { label: "耗時", value: fmtDuration(run.duration_seconds) },
            { label: "觸發者", value: run.create_name || run.create_id || "—" },
          ]}
        />
        <ErrorNote text={run.error_message || ""} />
        <KpiRow
          min={118}
          items={[
            {
              key: "repos",
              label: "Repo 成功",
              value: `${run.success_count}/${run.repo_count}`,
              color: Number(run.failed_count) > 0 ? "warning.main" : undefined,
              hint: Number(run.failed_count) > 0 ? `${run.failed_count} 個 repo 檢測失敗` : "",
            },
            { key: "bugs", label: "Bug", value: fmtCount(run.bugs), color: alertColor(run.bugs), hint: "僅加總檢測成功的 repo" },
            {
              key: "vuln",
              label: "弱點",
              value: fmtCount(run.vulnerabilities),
              color: alertColor(run.vulnerabilities),
              hint: "僅加總檢測成功的 repo",
            },
            { key: "smells", label: "Code Smell", value: fmtCount(run.code_smells), hint: "僅加總檢測成功的 repo" },
            { key: "hotspots", label: "Hotspot", value: fmtCount(run.security_hotspots), hint: "Security Hotspot，僅加總檢測成功的 repo" },
          ]}
        />
        {/*
          整批下載只在有兩個以上 repo 時才畫：只有一個 repo 時跟卡片裡那一顆下載的是同一份，
          兩顆一樣的按鈕只會讓人猶豫該按哪一顆。
        */}
        {results.length > 1 && hasStoredIssues(results) && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="outlined"
                sx={{ whiteSpace: "normal" }}
                onClick={() => downloadIssuesCsv({ run, project: proj, results })}
              >
                下載本次所有 repo 的問題清單（可用 Excel 開啟）
              </Button>
            </Stack>
            {/* 有任何一個 repo 被截掉，下載的檔案就不是全部 —— 那是後果，寫成提示條；都完整時只留一行說明 */}
            {covBanner ? (
              <Banner kind="warn" title={covBanner.sure ? "下載的檔案不完整" : "下載的檔案可能不完整"}>
                {covBanner.reasons.join("")}
                各 repo 缺少的筆數標示於下方卡片，檔案開頭亦有記載。
              </Banner>
            ) : (
              <Typography variant="body2" color="text.secondary">
                檔案開頭會列出各 repo 已儲存的筆數，以及是否完整。
              </Typography>
            )}
          </Stack>
        )}
        <Typography sx={{ fontWeight: 800 }}>
          各 repo 結果{" "}
          <Typography component="span" color="text.disabled">
            {results.length}
          </Typography>
        </Typography>
        {results.length === 0 ? (
          <EmptyState dense text="本次檢測無 repo 結果" />
        ) : (
          results.map((r) => (
            <RepoResultCard
              key={r.id}
              result={r}
              focused={r.id === focusRepoId}
              onDownload={(row) => downloadIssuesCsv({ run, project: proj, results: [row] })}
            />
          ))
        )}
      </Stack>
    );
  }

  return (
    <ScanDrawer
      open={open}
      onClose={onClose}
      title={`原碼檢測報告 #${run ? run.id : runId || ""}`}
      subtitle={proj ? [proj.code, proj.name].filter(Boolean).join(" · ") : ""}
      badges={
        run ? (
          <>
            <RunStatusPill
              status={Number(run.status)}
              durationSeconds={run.duration_seconds == null ? null : Number(run.duration_seconds)}
              cancelRequested={Boolean(run.cancel_requested)}
            />
            <GatePill gate={run.quality_gate || ""} />
          </>
        ) : null
      }
    >
      {body}
    </ScanDrawer>
  );
}

CodeReportDrawer.propTypes = {
  open: PropTypes.bool,
  runId: PropTypes.number,
  /** 從「檢測」頁點某個 repo 進來時的 result id（網址上的 ?repo=）；那張卡會捲到眼前、問題清單直接打開 */
  focusRepoId: PropTypes.number,
  onClose: PropTypes.func,
};

CodeReportDrawer.defaultProps = { open: false, runId: null, focusRepoId: null, onClose: () => {} };

/* ===================== 頁面 ===================== */

function CodeScanPage() {
  const { toast, confirmDelete } = useApp();
  const { projects, projectsLoaded, projId, project, setProjId, runId } = useScanPage(BASE);

  /*
   * 開關報告時要把 ?repo= 一起清掉，所以不用 useScanPage 的 openRun／closeRun（它們只管 run）。
   * 也不能先呼叫它的 closeRun、再另外刪 repo：react-router 6 的 setSearchParams 不會串接
   * 同一輪裡的兩次更新，後一次會拿舊網址把前一次蓋掉。所以這裡一次寫完兩個參數。
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const focusRepoId = Number(searchParams.get("repo")) || null;
  const patchReport = useCallback(
    (id) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("run", String(id));
          else next.delete("run");
          next.delete("repo");
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );
  const openRun = useCallback((id) => patchReport(id), [patchReport]);
  const closeRun = useCallback(() => patchReport(null), [patchReport]);

  const pid = project ? Number(project.id) : null;
  const runs = useRunHistory(BASE, pid);

  /* 沒權限就不放按鈕 —— 按了只會 403。權限碼與後端 CodeScanController 一字不差 */
  const canCreate = hasPermission("codescan.create");
  const canEdit = hasPermission("codescan.edit");
  const canDelete = hasPermission("codescan.delete");

  /* ---------- Git 連結 ---------- */
  const [repoData, setRepoData] = useState(null); // { project, repos }；null = 還沒載入
  const [repoFailed, setRepoFailed] = useState(false);
  const [draft, setDraft] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * 用後端回傳的清單重填 textarea —— 存完之後看到的是正規化過的網址（https 已轉 SSH、補上 .git），
   * 使用者才知道實際會拿去檢測的是什麼。
   * 含帳密的連結後端已遮成「帳號:***」：原樣留在框內、原樣送回，後端會換回原本存的帳密，
   * 所以框內文字與 savedRepos 都是遮蔽後的值，沒動就不算未儲存的變更。
   * 還沒有任何 repo 時先帶入專案資料上的 git_url（只帶入不存，要使用者自己確認後按儲存）；
   * 但剛存完一份空清單時不要再帶，否則使用者刻意清空後又冒出來，會以為沒存到。
   */
  const applyRepos = useCallback((d, allowPrefill) => {
    const repos = Array.isArray(d && d.repos) ? d.repos : [];
    const gitUrl = d && d.project ? d.project.git_url : "";
    setRepoData({ project: (d && d.project) || null, repos });
    if (allowPrefill && repos.length === 0 && gitUrl) {
      setDraft(String(gitUrl));
      setPrefilled(true);
    } else {
      setDraft(reposToText(repos));
      setPrefilled(false);
    }
  }, []);

  useEffect(() => {
    setRepoData(null);
    setRepoFailed(false);
    setDraft("");
    setPrefilled(false);
    if (!pid) return undefined;
    let alive = true;
    apiGet(`${BASE}/Repos`, { projectId: pid })
      .then((d) => {
        if (alive) applyRepos(d, true);
      })
      .catch((e) => {
        if (!alive) return;
        setRepoFailed(true);
        toast(apiErrorMessage(e, "載入 Git 連結失敗"), "error");
      });
    return () => {
      alive = false;
    };
  }, [pid, applyRepos, toast]);

  /*
   * 檢測從進行中變成結束時，重抓一次清單讓每個 repo 的「最近一次結果」跟上。
   * 只換 repos 不動 textarea：使用者可能正在編輯，不能把他打到一半的內容蓋掉。
   */
  const wasActiveRef = useRef({ pid: null, active: false });
  useEffect(() => {
    const prev = wasActiveRef.current;
    wasActiveRef.current = { pid, active: runs.hasActive };
    if (!pid || prev.pid !== pid || !prev.active || runs.hasActive) return;
    apiGet(`${BASE}/Repos`, { projectId: pid })
      .then((d) => {
        if (!d || !Array.isArray(d.repos)) return;
        setRepoData((cur) => (cur ? { ...cur, repos: d.repos } : cur));
      })
      .catch(() => {});
  }, [pid, runs.hasActive]);

  const savedRepos = repoData ? repoData.repos : [];
  const dirty = repoData != null && normalizedDraft(draft) !== reposToText(savedRepos);

  /*
   * 後端標出的舊資料（原碼檢測上線前就有的 bare path、沒轉 SSH 的 https…）：格式不能用（invalid），
   * 或能用但存的不是正規化後的樣子（needs_save）。要按一次儲存讓後端正規化 —— 不能用的那行儲存時會被指出來 ——
   * 之後畫面上的清單才等於實際拿去檢測的那份，所以儲存前不讓開始檢測
   */
  const invalidCount = savedRepos.filter((r) => r.invalid).length;
  const needsSave = savedRepos.some((r) => r.needs_save);
  const canSave = !busy && (dirty || needsSave);

  let repoWarning = "";
  if (needsSave && invalidCount > 0) {
    repoWarning = `有 ${invalidCount} 個 Git 連結格式無法使用（標示於下方清單），請修正後按儲存；儲存前無法開始檢測。`;
  } else if (needsSave) {
    repoWarning = "部分 Git 連結為舊格式，儲存後將轉換為檢測用的位址；儲存前無法開始檢測。";
  }
  if (repoWarning && !canEdit) repoWarning += "（您沒有維護 Git 連結的權限，請聯絡專案經理處理）";

  const saveRepos = () => {
    if (!pid || busy || !repoData) return;
    const lines = parseListLines(draft);
    // 密碼含空白時整行會在空白處被拆開：網址欄只剩「https://帳號:密碼前半」（冒號之後沒有 @ 也沒有 /），@主機 落到分支欄。
    // 先擋下，不把半截密碼送出去；網址欄是完整網址的，照下面「兩個網址或備註貼在同一行」處理
    const split = lines.find((x) => x.rest.includes("@") && /^(?:[a-z][a-z0-9+.-]*:\/\/)?[^\s/@:]*:[^\s/@]*$/i.test(x.url));
    if (split) {
      toast(`第 ${split.line} 行格式不正確：帳號密碼含空白時請先進行 URL 編碼（空白寫為 %20）`, "error");
      return;
    }
    // 分支名稱不會有空白、@ 或 ://；多出來的字多半是把兩個 repo 或備註貼在同一行
    const bad = lines.find((x) => /\s|@|:\/\//.test(x.rest));
    if (bad) {
      toast(`第 ${bad.line} 行格式不正確：網址後方僅能接一個分支名稱`, "error");
      return;
    }
    const repos = lines.map((x) => ({ url: x.url, branch: x.rest }));

    const doSave = async () => {
      setBusy(true);
      try {
        const d = await apiPutJson(`${BASE}/Repos`, { projectId: pid, repos });
        applyRepos(d, false);
        const n = Array.isArray(d && d.repos) ? d.repos.length : repos.length;
        toast(n ? `已儲存 ${n} 個 Git 連結` : "已清空 Git 連結");
      } catch (e) {
        toast(apiErrorMessage(e, "儲存 Git 連結失敗"), "error");
      } finally {
        setBusy(false);
      }
    };

    // 借 confirmDelete 的對話框（不是刪除，按鈕字樣自己指定）：少掉的 repo 之後就不會再被檢測
    if (repos.length < savedRepos.length) {
      confirmDelete({
        title: "移除部分 Git 連結？",
        subtitle: `清單由 ${savedRepos.length} 個變更為 ${repos.length} 個`,
        body: "不在清單上的 repo 之後將不再檢測；過去的檢測紀錄與報告仍會保留。",
        confirmText: "儲存",
        onConfirm: doSave,
      });
      return;
    }
    doSave();
  };

  /* ---------- 檢測動作 ---------- */

  const startScan = async () => {
    if (!pid || busy) return;
    setBusy(true);
    try {
      await apiPostJson(`${BASE}/Run`, { projectId: pid });
      toast("已排入檢測，完成後將通知您");
      runs.showFirstPage();
    } catch (e) {
      toast(apiErrorMessage(e, "排入檢測失敗"), "error");
    } finally {
      setBusy(false);
    }
  };

  /** 取消也要確認：跑到一半的檢測取消就得整批重跑，C# 專案光建置就要好幾分鐘 */
  const cancelRun = (r) =>
    confirmDelete({
      title: "停止本次檢測？",
      subtitle: r.status === 0 ? "排隊中，將直接取消" : "執行中，將於目前 repo 的步驟結束後停止",
      body: "已完成的 repo 結果將保留，尚未執行的 repo 將標示為已略過。",
      confirmText: "停止檢測",
      onConfirm: async () => {
        try {
          const d = await apiPostJson(`${BASE}/Run/${r.id}/Cancel`, {});
          // 以後端實際做了什麼為準：畫面上的狀態可能已經過時（例如檢測主機早就斷線，後端會直接標為失敗）
          let result = d && d.result;
          if (!result) result = r.status === 0 ? "cancelled" : "requested";
          if (result === "failed") toast("檢測主機逾時未回報，本次檢測已標示為失敗");
          else if (result === "cancelled") toast("已取消檢測");
          else toast("已要求停止，檢測主機將於目前步驟結束後停止");
          runs.refresh();
        } catch (e) {
          toast(apiErrorMessage(e, "取消檢測失敗"), "error");
        }
      },
    });

  const deleteRun = (r) =>
    confirmDelete({
      title: "刪除檢測紀錄？",
      subtitle: "此操作無法復原",
      body: `確定要刪除 ${fmtDateTime(r.queued_at)} 的檢測紀錄與報告嗎？SonarQube 上的分析結果不受影響。`,
      onConfirm: async () => {
        try {
          await apiDelete(`${BASE}/Run/${r.id}`);
          toast("已刪除檢測紀錄");
          if (runId === r.id) closeRun();
          runs.refresh();
        } catch (e) {
          toast(apiErrorMessage(e, "刪除檢測紀錄失敗"), "error");
        }
      },
    });

  /* 「開始檢測」停用原因：直接放在按鈕 title，滑過去就知道為什麼按不下去 */
  let startBlock = "";
  if (!repoData) startBlock = "Git 連結載入中…";
  else if (savedRepos.length === 0) startBlock = "請先設定 Git 連結並儲存";
  else if (needsSave && invalidCount > 0) startBlock = "有 Git 連結格式無法使用，請修正後按儲存";
  else if (needsSave) startBlock = "Git 連結須重新儲存（轉換為檢測用的位址）";
  else if (runs.hasActive) startBlock = "此專案已有檢測正在排隊或執行中";
  // 檢測用的是已儲存的清單；有改沒存就按下去，結果會跟畫面上的清單對不起來
  else if (dirty) startBlock = "Git 連結有未儲存的變更，請先儲存";
  else if (busy) startBlock = "處理中…";

  /* ---------- 最新結果 ---------- */
  const latest = runs.latest;
  const latestGate = latest ? GATE[String(latest.quality_gate || "").toUpperCase()] : null;

  /* ---------- 版面 ---------- */

  const historyBody = () => {
    const stateRow = (title) => (
      <TableRow>
        <TableCell colSpan={10}>
          <EmptyState dense text={title} />
        </TableCell>
      </TableRow>
    );
    if (runs.loading && runs.items.length === 0)
      return (
        <TableRow>
          <TableCell colSpan={10}>
            <SkeletonRows rows={5} />
          </TableCell>
        </TableRow>
      );
    if (runs.failed) return stateRow("檢測歷史載入失敗，請稍後再試");
    if (runs.items.length === 0) return stateRow("尚無檢測紀錄，請設定 Git 連結後點選右上方「開始檢測」");
    return runs.items.map((r) => {
      const active = isActiveStatus(r.status);
      return (
        <TableRow key={r.id}>
          <TableCell>{fmtDateTime(r.queued_at)}</TableCell>
          <TableCell>{r.create_name || r.create_id || "—"}</TableCell>
          <TableCell>
            <RunStatusPill status={r.status} durationSeconds={r.duration_seconds} cancelRequested={r.cancel_requested} />
          </TableCell>
          <TableCell align="right">{fmtDuration(r.duration_seconds)}</TableCell>
          <TableCell align="right">
            {r.success_count}/{r.repo_count}
            {Number(r.failed_count) > 0 && (
              <Box component="span" sx={{ color: "error.main", fontWeight: 700, ml: 0.75 }}>
                失敗 {r.failed_count}
              </Box>
            )}
          </TableCell>
          <TableCell>
            <GatePill gate={r.quality_gate || ""} />
          </TableCell>
          <TableCell align="right" sx={alertColor(r.bugs) ? { color: "error.main !important" } : undefined}>
            {fmtCount(r.bugs)}
          </TableCell>
          <TableCell align="right" sx={alertColor(r.vulnerabilities) ? { color: "error.main !important" } : undefined}>
            {fmtCount(r.vulnerabilities)}
          </TableCell>
          <TableCell align="right">{fmtCount(r.code_smells)}</TableCell>
          <TableCell sx={{ py: 0.5 }}>
            <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
              <IconButton icon="view" title="檢視報告" onClick={() => openRun(r.id)} />
              {canCreate && active && (
                <IconButton
                  icon="cancel"
                  title={r.cancel_requested ? "已要求停止，等待檢測主機停止" : "停止檢測"}
                  disabled={r.cancel_requested}
                  onClick={() => cancelRun(r)}
                />
              )}
              {canDelete && !active && <IconButton icon="delete" title="刪除紀錄" onClick={() => deleteRun(r)} />}
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  };

  const renderMain = () => {
    if (!projId) {
      return (
        <Card>
          {projectsLoaded && projects.length === 0 ? (
            <EmptyState title="沒有可檢測的專案" text="目前沒有可檢視的專案；請專案經理將您加入專案成員。" />
          ) : (
            <EmptyState title="請先選擇專案" text="選擇專案後，即可設定 Git 連結並開始檢測。" />
          )}
        </Card>
      );
    }
    if (!project) {
      return (
        <Card>
          {projectsLoaded ? (
            <EmptyState title="找不到此專案" text="專案可能已刪除，或不在您的檢視範圍內。" />
          ) : (
            <PageLoading dense />
          )}
        </Card>
      );
    }

    return (
      <>
        {/* Git 連結 */}
        <Card>
          <SectionHead>
            <span>Git 連結</span>
            <CountChip>已儲存 {savedRepos.length} 個 repo</CountChip>
            {dirty && <HeadNote color="warning.main">有未儲存的變更</HeadNote>}
            <Box sx={{ flex: 1 }} />
            {canEdit && (
              <Tooltip title={needsSave && !dirty ? "重新儲存，將舊格式的連結轉換為檢測用的位址" : ""}>
                <span>
                  <Button variant="outlined" onClick={saveRepos} disabled={!canSave}>
                    儲存
                  </Button>
                </span>
              </Tooltip>
            )}
          </SectionHead>
          <Stack spacing={1.25} sx={{ px: 3, py: 2 }}>
            {prefilled && <Banner kind="warn">已帶入專案的 Git 連結，確認後請按儲存</Banner>}
            {repoWarning && <Banner kind={invalidCount > 0 ? "attention" : "warn"}>{repoWarning}</Banner>}
            {repoFailed ? (
              <EmptyState dense text="Git 連結載入失敗，請重新整理後再試" />
            ) : (
              <TextField
                multiline
                minRows={6}
                fullWidth
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setPrefilled(false);
                }}
                placeholder={
                  repoData
                    ? "git@git.lifeplus.net.tw:group/api.git\nhttps://git.lifeplus.net.tw/group/frontend develop"
                    : "載入中…"
                }
                title={canEdit ? "" : "您沒有維護 Git 連結的權限"}
                slotProps={{
                  htmlInput: { readOnly: !canEdit || !repoData, spellCheck: false, "aria-label": "Git 連結", "aria-describedby": "codescan-repo-help" },
                }}
                sx={{ "& textarea": { ...MONO, fontSize: "var(--t-code)" } }}
              />
            )}
            <Typography id="codescan-repo-help" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              每行一筆，格式為「網址 分支」；# 開頭為註解；分支留空代表預設分支。可貼上 https 或 SSH 網址，https 將自動轉為 SSH（含帳密的 https 連結保留 https）；檢測主機以設定好的 GitLab Token 或 Deploy Key 存取，Gitea 等無 Token 的主機可使用 https://帳號:密碼@主機/群組/repo，帳號密碼含特殊字元時請先進行 URL 編碼；儲存後密碼以 *** 顯示，無須重新輸入，如需更換請貼上新的完整網址。
            </Typography>
          </Stack>
          {/* 每個 repo 最近一次的結果：不用開報告就看得出哪個 repo 有狀況 */}
          {savedRepos.length > 0 && (
            <Box sx={{ borderTop: 1, borderColor: "divider" }}>
              {savedRepos.map((r) => {
                const last = r.last;
                const lastSt = last ? Number(last.status) : null;
                return (
                  <Typography
                    component="div"
                    variant="body2"
                    key={r.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      flexWrap: "wrap",
                      px: 3,
                      py: 1.25,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      component="span"
                      title={r.url}
                      sx={{ ...MONO, flex: 1, minWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {r.url}
                    </Box>
                    <Box component="span" sx={{ ...MONO, color: "text.secondary" }}>
                      {r.branch || "預設分支"}
                    </Box>
                    {r.invalid && (
                      <Box component="span" sx={{ color: "error.main", fontWeight: 700, whiteSpace: "nowrap" }}>
                        格式無法使用
                      </Box>
                    )}
                    {!r.invalid && r.needs_save && (
                      <Box component="span" sx={{ color: "warning.main", fontWeight: 700, whiteSpace: "nowrap" }}>
                        需重新儲存
                      </Box>
                    )}
                    {!last && (
                      <Box component="span" sx={{ color: "text.disabled", fontWeight: 600 }}>
                        尚未檢測
                      </Box>
                    )}
                    {last && lastSt === 2 && <GatePill gate={last.quality_gate || "NONE"} />}
                    {last && lastSt !== 2 && <MetaPill meta={ITEM_STATUS[lastSt]} dot={lastSt === 1} />}
                    {last && (
                      <Box component="span" sx={{ color: "text.disabled", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {fmtDateTime(last.analysis_at || last.finished_at)}
                      </Box>
                    )}
                  </Typography>
                );
              })}
            </Box>
          )}
        </Card>

        {/* 最新結果 */}
        <Card>
          <SectionHead>
            <span>最新結果</span>
            {latest && (
              <HeadNote>
                {fmtDateTime(latest.finished_at || latest.queued_at)} · {latest.success_count}/{latest.repo_count} 個 repo 成功
              </HeadNote>
            )}
            <Box sx={{ flex: 1 }} />
            {latest && (
              <Button variant="outlined" onClick={() => openRun(latest.id)}>
                檢視報告
              </Button>
            )}
          </SectionHead>
          <Box sx={{ px: 3, py: 2 }}>
            {latest ? (
              <KpiRow
                items={[
                  {
                    key: "gate",
                    label: "品質門檻",
                    value: latestGate ? latestGate.label : "—",
                    color: latestGate ? latestGate.c : undefined,
                  },
                  { key: "bugs", label: "Bug", value: fmtCount(latest.bugs), color: alertColor(latest.bugs) },
                  {
                    key: "vuln",
                    label: "弱點",
                    value: fmtCount(latest.vulnerabilities),
                    color: alertColor(latest.vulnerabilities),
                  },
                  { key: "smells", label: "Code Smell", value: fmtCount(latest.code_smells) },
                  { key: "hotspots", label: "Security Hotspot", value: fmtCount(latest.security_hotspots) },
                ]}
              />
            ) : (
              <EmptyState dense text="尚無已完成的檢測" />
            )}
          </Box>
        </Card>

        {/* 檢測歷史 */}
        <Card>
          <SectionHead>
            <span>檢測歷史</span>
            <CountChip>共 {runs.total} 次</CountChip>
            {runs.hasActive && <HeadNote>有檢測進行中，每 5 秒自動更新</HeadNote>}
          </SectionHead>
          <TableContainer>
            <Table sx={{ minWidth: 960 }}>
              <TableHead sx={{ bgcolor: "background.default" }}>
                <TableRow>
                  <TableCell>開始時間</TableCell>
                  <TableCell>觸發者</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell align="right">耗時</TableCell>
                  <TableCell align="right">Repo（成功/總數）</TableCell>
                  <TableCell>品質門檻</TableCell>
                  <TableCell align="right">Bug</TableCell>
                  <TableCell align="right">弱點</TableCell>
                  <TableCell align="right">Code Smell</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={tableBodySx}>{historyBody()}</TableBody>
            </Table>
          </TableContainer>
          <ListPager
            page={runs.page}
            totalPages={runs.totalPages}
            total={runs.total}
            onPrev={() => runs.setPage(runs.page - 1)}
            onNext={() => runs.setPage(runs.page + 1)}
          />
        </Card>
      </>
    );
  };

  return (
    // 抽屜（MUI Drawer）自己 portal 到 body，放在 .pg 外面只是為了讀起來清楚
    <>
      <Stack spacing={2} className="pg">
        <Card sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", px: 2, py: 1.75 }}>
          <TextField
            select
            label="專案"
            value={projId}
            onChange={(e) => setProjId(e.target.value)}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            sx={{ minWidth: 220, maxWidth: "100%" }}
          >
            <option value="">請選擇專案</option>
            {projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.code ? `${p.code} ${p.name}` : p.name}
              </option>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          {canCreate && project && (
            /* 停用的按鈕收不到滑鼠事件，停用原因改由外層 span 的 Tooltip 顯示 */
            <Tooltip title={startBlock || `檢測已儲存的 ${savedRepos.length} 個 repo`}>
              <span>
                <Button variant="contained" onClick={startScan}
                    disabled={Boolean(startBlock)}
                    loading={busy}
                    loadingPosition="start"
                    startIcon={<MdPlayArrow />}
                  >
                  開始檢測
                </Button>
              </span>
            </Tooltip>
          )}
        </Card>

        {renderMain()}
      </Stack>

      {/* 深連結 ?run= 等專案清單載入後才開，底下的頁面先就位，關掉抽屜時不會看到一片空白 */}
      <CodeReportDrawer open={Boolean(runId) && projectsLoaded} runId={runId} focusRepoId={focusRepoId} onClose={closeRun} />
    </>
  );
}

/* 狀態全在頁內與網址列，不接 redux，沒有外部 props（同 Project/Add.js 的寫法） */
CodeScanPage.propTypes = {};
CodeScanPage.defaultProps = {};

export default CodeScanPage;
